import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
  Button,
  Icon,
  useToast,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  FormControl,
  FormLabel,
  Switch,
  Tooltip,
  Container,
  IconButton,
  Divider,
  VStack
} from '@chakra-ui/react';
import { FiRefreshCw, FiSettings } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../layout/AdminLayout';
import { useLogout } from '../utils/authUtils';
import { narrationScript } from '../../data/narrationScript';
import { db as presentationDb } from '../../firebase/firebase';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { SlideNarration as ImportedSlideNarration, DashboardNarration, NarrationStatus } from '../../types/narration';
import { 
  fetchNarrations, 
  getPhoneticText, 
  useNarrationSync,
  updateNarrationWithAudio, 
  uploadNarrationAudio,
  migrateToUnifiedCollection,
  mapNarrationsToAudioFiles
} from '../../scripts/unifiedNarrationSync';
import { updateAllPhoneticNarrations } from '../../scripts/updatePhoneticNarrations';
import { updateNarrationTitles } from '../../scripts/updateNarrationTitles';
import { removeDuplicateNarrations } from '../../scripts/removeDuplicateNarrations';

import NarrationList from './components/NarrationList';
import NarrationEditor from './components/NarrationEditor';
import ElevenLabsPanel from './components/ElevenLabsPanel';
import AudioUploadDebugger from './components/AudioUploadDebugger';

// SentientIQ brand colors
const sentientIqBlue = '#0066CC';
const sentientIqNavy = '#002D72';
const sentientIqGreen = '#38A169';

// Layout constants
const LEFT_COLUMN_WIDTH = '250px';
const RIGHT_COLUMN_WIDTH = '280px';

// Local SlideNarration type that extends the imported one
type LocalSlideNarration = ImportedSlideNarration & {
  hasDynamicContent?: boolean;
  lastUpdated: Date;
};

// Helper function to create a properly typed SlideNarration object
const createSlideNarration = (baseNarration: any, overrides: Partial<LocalSlideNarration> = {}): LocalSlideNarration => {
  return {
    ...baseNarration,
    title: overrides.title || baseNarration?.title || `Slide ${baseNarration?.slideId || 'Unknown'}`,
    phoneticNarration: overrides.phoneticNarration || baseNarration?.phoneticNarration || '',
    status: overrides.status || baseNarration?.status || 'pending',
    type: 'slide' as const,
    id: baseNarration?.slideId || '',
    text: overrides.text || baseNarration?.text || baseNarration?.narration || '',
    lastUpdated: overrides.lastUpdated || new Date(),
    hasDynamicContent: overrides.hasDynamicContent || false
  } as LocalSlideNarration;
};

const TTSStudioPage = () => {
  const logout = useLogout();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDebuggerOpen, onOpen: onOpenDebugger, onClose: onCloseDebugger } = useDisclosure();
  
  // Narration sync hook
  const {
    updateNarration,
    createSlideNarration: createSyncedSlideNarration,
    createDashboardNarration: createSyncedDashboardNarration,
    updateNarrationWithAudio: updateAudio
  } = useNarrationSync();
  
  // Define local state for sync status tracking
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Local state
  const [scriptData, setScriptData] = useState<LocalSlideNarration[]>([]);
  const [selectedNarrationId, setSelectedNarrationId] = useState<string | null>(null);
  const [selectedNarration, setSelectedNarration] = useState<LocalSlideNarration | null>(null);
  const [narrationText, setNarrationText] = useState<string>('');
  const [phoneticText, setPhoneticText] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [isRemovingDuplicates, setIsRemovingDuplicates] = useState<boolean>(false);
  const [isMigrating, setIsMigrating] = useState<boolean>(false);
  
  // State for tracking audio mapping status
  const [isMappingAudio, setIsMappingAudio] = useState(false);
  
  // State for tracking refresh operation
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Sync settings
  const [autoSyncEnabled, setAutoSyncEnabled] = useState<boolean>(
    localStorage.getItem('ttsStudioAutoSync') === 'true'
  );
  const [syncInterval, setSyncInterval] = useState<number>(
    parseInt(localStorage.getItem('ttsStudioSyncInterval') || '60000')
  );
  
  // Handle narration selection
  const handleNarrationSelect = useCallback((narrationId: string) => {
    const narration = scriptData.find(n => n.slideId === narrationId);
    if (narration) {
      console.log('Selected narration:', {
        id: narration.slideId,
        title: narration.title,
        text: narration.text,
        hasAudio: !!narration.audioUrl,
        audioUrl: narration.audioUrl
      });
      
      setSelectedNarrationId(narrationId);
      setSelectedNarration(narration);
      setNarrationText(narration.text || '');
      setPhoneticText(narration.phoneticNarration || '');
    }
  }, [scriptData]);

  // Function to load narrations from Firebase
  const loadNarrations = async () => {
    try {
      setIsRefreshing(true);
      setSyncStatus('syncing');
      
      // Update narration titles first to ensure consistent branding
      const { success, totalUpdated } = await updateNarrationTitles();
      if (success && totalUpdated > 0) {
        console.log(`Updated ${totalUpdated} narration titles with consistent branding`);
        toast({
          title: 'Narration titles updated',
          description: `Updated ${totalUpdated} narrations with consistent SentientIQ branding`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'bottom-right'
        });
      }

      // Update phonetic narrations to ensure Firebase has the latest data
      await updateAllPhoneticNarrations()
        .then(results => {
          if (results.success && results.totalUpdated > 0) {
            console.log(`Updated ${results.totalUpdated} narrations with phonetic text`);
            toast({
              title: 'Phonetic narrations updated',
              description: `Updated ${results.totalUpdated} narrations with improved pronunciation`,
              status: 'success',
              duration: 5000,
              isClosable: true,
              position: 'bottom-right'
            });
          }
        })
        .catch(error => {
          console.error('Error updating phonetic narrations:', error);
        });
      
      // Fetch narrations from Firebase (from both slide_narrations and dashboard_narrations collections)
      const narrationData = await fetchNarrations();
      console.log('Fetched narrations from Firebase:', narrationData.length);
      
      // Debug: Log all narration IDs to see what we're getting
      console.log('Narration IDs:', narrationData.map(n => ({ id: n.id, type: n.type, title: n.title })));
      
      const slideNarrations = narrationData
        .filter(n => n.type === 'slide')
        .map(n => ({
          ...n,
          type: 'slide' as const,
          id: n.id,
          slideId: n.id,
          text: n.text || '',
          title: n.title || '',
          phoneticNarration: n.phoneticNarration || getPhoneticText(n),
          status: n.status || 'pending',
          audioUrl: n.audioUrl || null,
          lastUpdated: n.lastUpdated || new Date(),
          hasDynamicContent: (n as any).hasDynamicContent || false
        })) as LocalSlideNarration[];
      
      // Log narrations with audio URLs for debugging
      console.log('Narrations with audio URLs:', 
        slideNarrations
          .filter(n => n.audioUrl)
          .map(n => ({ id: n.slideId, title: n.title, audioUrl: n.audioUrl }))
      );
      
      // Check specifically for SentientIQ Companion narration
      const aiCompanionNarration = slideNarrations.find(n => n.slideId === 'ai-companion');
      if (aiCompanionNarration) {
        console.log('SentientIQ Companion narration:', {
          id: aiCompanionNarration.slideId,
          title: aiCompanionNarration.title,
          status: aiCompanionNarration.status,
          hasAudio: !!aiCompanionNarration.audioUrl,
          audioUrl: aiCompanionNarration.audioUrl
        });
      } else {
        console.log('SentientIQ Companion narration not found');
      }
      
      // Log the total number of narrations loaded
      console.log(`Loaded ${slideNarrations.length} narrations from Firebase`);
      
      setScriptData(slideNarrations);
      
      // Update sync status and time
      setSyncStatus('success');
      setLastSyncTime(new Date());

      toast({
        title: 'Narrations refreshed',
        description: `Successfully loaded ${slideNarrations.length} narrations from Firebase`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      });
    } catch (error) {
      console.error('Error loading narrations:', error);
      setSyncStatus('error');
      toast({
        title: 'Error loading narrations',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Load narrations on mount
  useEffect(() => {
    loadNarrations();
  }, [toast]);

  // Save narration changes
  const handleSaveNarration = async () => {
    if (!selectedNarrationId || !selectedNarration) return;
    
    setIsSaving(true);
    try {
      // Generate phonetic text from the standard narration
      const generatedPhoneticText = narrationText
        // Ensure SentientIQ is spelled correctly (not SentienIQ)
        .replace(/SentienIQ/g, 'SentientIQ')
        .replace(/sentieniq/gi, 'SentientIQ')
        // Handle common medical abbreviations
        .replace(/Dr\./g, 'Doctor')
        .replace(/\bMD\b/g, 'M D')
        .replace(/\bEHR\b/g, 'E H R')
        .replace(/\bAI\b/g, 'A I')
        .replace(/\bML\b/g, 'M L')
        .replace(/\bJIMI\b/g, 'Jimmy');
      
      // Update the phoneticText state
      setPhoneticText(generatedPhoneticText);
      
      const updatedNarration: LocalSlideNarration = {
        ...selectedNarration,
        text: narrationText,
        phoneticNarration: generatedPhoneticText
      };
      
      await updateNarration(updatedNarration);
      
      toast({
        title: 'Narration saved',
        description: 'Your changes have been saved successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      });
      
      // Update local state
      setScriptData(prevData => 
        prevData.map(n => 
          n.slideId === selectedNarrationId ? updatedNarration : n
        )
      );
      
      setSelectedNarration(updatedNarration);
      
    } catch (error) {
      console.error('Error saving narration:', error);
      toast({
        title: 'Error saving narration',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle audio upload success
  const handleAudioUploadSuccess = async (audioUrl: string, narrationId?: string) => {
    try {
      const targetNarrationId = narrationId || selectedNarrationId;
      if (!targetNarrationId) {
        throw new Error('No narration selected');
      }

      const narration = scriptData.find(n => n.slideId === targetNarrationId);
      if (!narration) {
        throw new Error('Narration not found');
      }
      
      await updateAudio(targetNarrationId, audioUrl, 'slide' as const);
      
      // Update local state
      setScriptData(prevData => 
        prevData.map(n => 
          n.slideId === targetNarrationId 
            ? { ...n, audioUrl, status: 'ready' as NarrationStatus, lastUpdated: new Date() } 
            : n
        )
      );
      
      if (selectedNarrationId === targetNarrationId) {
        setSelectedNarration(prev => 
          prev ? { ...prev, audioUrl, status: 'ready', lastUpdated: new Date() } : null
        );
      }
      
      toast({
        title: 'Audio uploaded',
        description: 'Audio file has been uploaded and linked successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      });
      
    } catch (error) {
      console.error('Error handling audio upload:', error);
      toast({
        title: 'Error updating narration',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
    }
  };

  // Handle duplicate removal
  const handleRemoveDuplicates = async () => {
    setIsRemovingDuplicates(true);
    try {
      await updateNarrationTitles();
      
      // Refresh narrations
      const narrations = await fetchNarrations();
      if (narrations) {
        const slideNarrations = narrations
          .filter(n => n.type === 'slide')
          .map(n => ({
            ...n,
            type: 'slide' as const,
            id: n.id,
            slideId: n.id,
            text: n.text || '',
            title: n.title || '',
            phoneticNarration: n.phoneticNarration || getPhoneticText(n),
            status: n.status || 'pending',
            audioUrl: n.audioUrl || null,
            lastUpdated: n.lastUpdated || new Date(),
            hasDynamicContent: (n as any).hasDynamicContent || false
          })) as LocalSlideNarration[];
        setScriptData(slideNarrations);
      }
      
      toast({
        title: 'Duplicates removed',
        description: 'Duplicate narrations have been successfully removed.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
    } catch (error) {
      console.error('Error removing duplicates:', error);
      toast({
        title: 'Error removing duplicates',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
    } finally {
      setIsRemovingDuplicates(false);
    }
  };

  // Handle narration migration to unified collection
  const handleMigrateToUnifiedCollection = async () => {
    if (isMigrating) return;
    
    try {
      setIsMigrating(true);
      
      const result = await migrateToUnifiedCollection();
      
      toast({
        title: 'Migration completed',
        description: `Successfully migrated ${result.migrated} narrations and deleted ${result.deleted} legacy documents. Errors: ${result.errors}`,
        status: result.errors > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
      
      // Refresh narrations after migration
      const narrations = await fetchNarrations();
      if (narrations) {
        const slideNarrations = narrations
          .filter(n => n.type === 'slide')
          .map(n => ({
            ...n,
            type: 'slide' as const,
            id: n.id,
            slideId: n.id,
            text: n.text || '',
            title: n.title || '',
            phoneticNarration: n.phoneticNarration || getPhoneticText(n),
            status: n.status || 'pending',
            audioUrl: n.audioUrl || null,
            lastUpdated: n.lastUpdated || new Date(),
            hasDynamicContent: (n as any).hasDynamicContent || false
          })) as LocalSlideNarration[];
        setScriptData(slideNarrations);
      }
    } catch (error) {
      console.error('Error migrating narrations:', error);
      toast({
        title: 'Migration failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
    } finally {
      setIsMigrating(false);
    }
  };

  // Handle mapping narrations to audio files in Firebase Storage
  const handleMapNarrationsToAudioFiles = async () => {
    if (isMappingAudio) return;
    
    try {
      setIsMappingAudio(true);
      
      const result = await mapNarrationsToAudioFiles();
      
      toast({
        title: 'Audio mapping completed',
        description: `Processed ${result.total} audio files, updated ${result.updated} narrations, ${result.statusUpdated} status updates. Errors: ${result.errors}, Unmapped files: ${result.unmapped.length}`,
        status: result.errors > 0 || result.unmapped.length > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
      
      // Refresh narrations after mapping
      const narrations = await fetchNarrations();
      if (narrations) {
        const slideNarrations = narrations
          .filter(n => n.type === 'slide')
          .map(n => ({
            ...n,
            type: 'slide' as const,
            id: n.id,
            slideId: n.id,
            text: n.text || '',
            title: n.title || '',
            phoneticNarration: n.phoneticNarration || getPhoneticText(n),
            status: n.status || 'pending',
            audioUrl: n.audioUrl || null,
            lastUpdated: n.lastUpdated || new Date(),
            hasDynamicContent: (n as any).hasDynamicContent || false
          })) as LocalSlideNarration[];
        setScriptData(slideNarrations);
      }
    } catch (error) {
      console.error('Error mapping narrations to audio files:', error);
      toast({
        title: 'Audio mapping failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
    } finally {
      setIsMappingAudio(false);
    }
  };

  return (
    <AdminLayout title="TTS Studio" onLogout={logout.handleLogout}>
      <Box p={4}>
        {/* Header with refresh and settings buttons */}
        <Flex justifyContent="flex-end" mb={4}>
          <Tooltip label="Refresh narrations from Firebase" placement="top">
            <IconButton
              aria-label="Refresh narrations"
              icon={<FiRefreshCw />}
              colorScheme="blue"
              variant="outline"
              mr={2}
              isLoading={isRefreshing}
              onClick={loadNarrations}
            />
          </Tooltip>
          <Tooltip label="Settings" placement="top">
            <IconButton
              aria-label="Settings"
              icon={<FiSettings />}
              colorScheme="gray"
              variant="outline"
              onClick={onOpen}
            />
          </Tooltip>
        </Flex>

        <Grid
          templateColumns={`${LEFT_COLUMN_WIDTH} 1fr ${RIGHT_COLUMN_WIDTH}`}
          gap={4}
          height="calc(100vh - 100px)"
        >
          {/* Left Column - Narration List */}
          <GridItem height="calc(100vh - 120px)" overflow="hidden" display="flex" flexDirection="column">
            <NarrationList
              narrations={scriptData}
              selectedNarrationId={selectedNarrationId}
              onSelect={handleNarrationSelect}
            />
          </GridItem>

          {/* Middle Column - Narration Editor */}
          <GridItem>
            <NarrationEditor
              narrationText={narrationText}
              phoneticText={phoneticText}
              onNarrationChange={setNarrationText}
              onPhoneticChange={setPhoneticText}
              onSave={handleSaveNarration}
              isLoading={isSaving}
            />
          </GridItem>

          {/* Right Column - ElevenLabs Panel */}
          <GridItem>
            <ElevenLabsPanel
              narrationId={selectedNarration?.slideId || null}
              narrationText={selectedNarration?.text || ''}
              phoneticText={selectedNarration?.phoneticNarration || ''}
              existingAudioUrl={selectedNarration?.audioUrl}
              onAudioUploaded={handleAudioUploadSuccess}
            />
          </GridItem>
        </Grid>

        {/* Settings Drawer */}
        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={onClose}
          size="md"
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">
              TTS Studio Settings
            </DrawerHeader>

            <DrawerBody>
              <FormControl display="flex" alignItems="center" mb={4}>
                <FormLabel htmlFor="auto-sync" mb="0">
                  Auto-sync with Firebase
                </FormLabel>
                <Switch
                  id="auto-sync"
                  isChecked={autoSyncEnabled}
                  onChange={(e) => {
                    setAutoSyncEnabled(e.target.checked);
                    localStorage.setItem('ttsStudioAutoSync', e.target.checked.toString());
                  }}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel htmlFor="sync-interval">
                  Sync Interval (milliseconds)
                </FormLabel>
                <input
                  type="number"
                  id="sync-interval"
                  value={syncInterval}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setSyncInterval(value);
                    localStorage.setItem('ttsStudioSyncInterval', value.toString());
                  }}
                  min="1000"
                  step="1000"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '4px'
                  }}
                />
              </FormControl>

              <Button
                colorScheme="red"
                width="100%"
                mb={4}
                onClick={handleRemoveDuplicates}
                isLoading={isRemovingDuplicates}
              >
                Remove Duplicate Narrations
              </Button>

              <Button
                colorScheme="blue"
                width="100%"
                mb={4}
                onClick={handleMigrateToUnifiedCollection}
                isLoading={isMigrating}
              >
                Migrate to Unified Collection
              </Button>

              <Button
                colorScheme="blue"
                width="100%"
                mb={4}
                onClick={handleMapNarrationsToAudioFiles}
                isLoading={isMappingAudio}
              >
                Map Narrations to Audio Files
              </Button>

              <Button
                colorScheme="blue"
                width="100%"
                onClick={onOpenDebugger}
              >
                Open Audio Upload Debugger
              </Button>
            </DrawerBody>

            <DrawerFooter borderTopWidth="1px">
              <Button variant="outline" mr={3} onClick={onClose}>
                Close
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        {/* Audio Upload Debugger */}
        <Drawer
          isOpen={isDebuggerOpen}
          placement="right"
          onClose={onCloseDebugger}
          size="xl"
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">
              Audio Upload Debugger
            </DrawerHeader>

            <DrawerBody>
              <Box>
                <AudioUploadDebugger
                  onUpload={handleAudioUploadSuccess}
                  narrations={scriptData.map(n => ({
                    id: n.slideId,
                    title: n.title,
                    audioUrl: n.audioUrl || null
                  }))}
                />
              </Box>
            </DrawerBody>

            <DrawerFooter borderTopWidth="1px">
              <Button variant="outline" onClick={onCloseDebugger}>
                Close
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </Box>
    </AdminLayout>
  );
};

export default TTSStudioPage;
